// src/views/LeadFacts.jsx
//
// "On file — what we know" on a lead (Reece, 10/10/26). Engine:
// src/convoGuide.js (pure, read-only). Reece: "just notes from any
// information that I already have that may be helpful" — no fields to fill
// in, no "add a note" box, no talking points or questions.
//
// A compact block: the "Likely fit: … — a guide, could change" chip (only
// when the lead carries a fit:* / class:* tag), the first COLLAPSED_LINES
// facts (+ the litigator / do-not-contact lines, always), and a "Show all N"
// button for the rest. The ONLY control in it is
// that button. It reads only the record already on screen plus, for Reece's
// login, a COUNT of the skip-traced contacts Outbound holds for the lead
// (lib.outboundByLead; Max's access map has outbound:false, so his login
// never asks). It writes nothing. The open / closed choice is a per-browser
// convenience (localStorage, try/catch).
//
// Mounted in the New Call lead card's "On file" section (the host draws that
// heading — no `title`), and with its own heading (`title`) in Marketing's
// lead detail and Inquiry's Lead Details.

import React, { useEffect, useId, useMemo, useState } from "react";
import { C } from '../theme';
import { leadFactsFor, fitLabelText, outboundCounts, collapsedFacts, ON_FILE_TITLE } from '../convoGuide';
import { outboundByLead } from '../lib';
import { staffAccess } from '../access';

const OPEN_KEY="loanflow_lead_facts_open";
function readOpen(){try{return window.localStorage.getItem(OPEN_KEY)==="1";}catch(e){return false;}}
function writeOpen(v){try{window.localStorage.setItem(OPEN_KEY,v?"1":"0");}catch(e){}}

// Session cache of the counts per lead (10 min), so reopening a card doesn't
// ask again. Counts only — the rows themselves are never kept.
const OB_TTL_MS=10*60*1000;
const obCache=new Map();
export function clearOutboundCountCache(){obCache.clear();}
export function useOutboundCounts(leadId,enabled){
  const cached=enabled&&leadId?obCache.get(leadId):null;
  const fresh=cached&&Date.now()-cached.at<OB_TTL_MS?cached.counts:null;
  const [state,setState]=useState({id:null,counts:null});
  useEffect(function(){
    if(!enabled||!leadId)return undefined;
    const c=obCache.get(leadId);
    if(c&&Date.now()-c.at<OB_TTL_MS)return undefined;
    let live=true;
    Promise.resolve().then(function(){return outboundByLead(leadId);}).then(function(r){
      if(!live)return;
      if(r&&!r.error&&!r.missing){
        const counts=outboundCounts(r.data);
        obCache.set(leadId,{at:Date.now(),counts:counts});
        setState({id:leadId,counts:counts});
      }else setState({id:leadId,counts:null});
    },function(){if(live)setState({id:leadId,counts:null});});
    return function(){live=false;};
  },[leadId,enabled]);
  if(!enabled||!leadId)return null;
  return fresh||(state.id===leadId?state.counts:null);
}

const LABEL={flex:"0 0 116px",maxWidth:"100%",fontSize:11.5,fontWeight:700,color:C.textMuted,margin:0};
const VALUE={flex:"1 1 220px",minWidth:0,margin:0,fontSize:12.5,lineHeight:1.45,fontWeight:600,color:C.text,overflowWrap:"anywhere"};
const GROUP={fontSize:10,fontWeight:800,color:C.textMuted,textTransform:"uppercase",letterSpacing:.6,margin:"4px 0 0"};

// lead: the marketing_leads row. loans: the files in memory (the linked file).
// staff: who is signed in. skipFileId: the file already on screen.
// title: draw the "On file — what we know" heading (hosts without one).
export function LeadFacts({lead,loans,staff,skipFileId,now,title,style}){
  const access=staffAccess(staff);
  const leadId=lead&&lead.id?lead.id:null;
  const outbound=useOutboundCounts(leadId,!!access.outbound);
  const facts=useMemo(function(){return leadFactsFor(lead,{now:now,loans:loans,outbound:outbound,skipFileId:skipFileId});},[lead,loans,outbound,skipFileId,now]);
  const [open,setOpen]=useState(readOpen);
  const regionId=useId();
  if(!lead||(!facts.fit&&!facts.lines.length))return null;
  const lines=facts.lines;
  // Collapsed: the first COLLAPSED_LINES + the litigator / do-not-contact
  // lines wherever they fall (convoGuide collapsedFacts — never behind "Show all").
  const few=collapsedFacts(lines);
  const extra=lines.length-few.length;
  const shown=open||!extra?lines:few;
  const toggle=function(){setOpen(function(v){writeOpen(!v);return !v;});};
  const hasProperty=lines.some(function(l){return l.group==="property";});
  // One <dl> per group (the property, then the history with us), so the
  // history heading sits between lists, never inside one.
  const groups=[];
  shown.forEach(function(l){
    const g=groups[groups.length-1];
    if(g&&g.key===l.group)g.lines.push(l);else groups.push({key:l.group,lines:[l]});
  });
  const line=function(l){
    return <div key={l.key} data-fact={l.key} data-group={l.group} style={{display:"flex",flexWrap:"wrap",alignItems:"baseline",gap:"0 10px",minWidth:0}}>
      <dt style={Object.assign({},LABEL,l.warn?{color:C.clayDeep}:null)}>{l.label}</dt>
      <dd style={VALUE}>{l.text}{l.note&&<span data-fact-note="" style={{fontWeight:500,color:C.textMuted}}> — {l.note}</span>}</dd>
    </div>;
  };
  const body=(
    <div data-lead-facts="" role="group" aria-label={ON_FILE_TITLE} style={Object.assign({minWidth:0},title?{}:style||{})}>
      {facts.fit&&<div style={{marginBottom:lines.length?8:0}}>
        <span data-fit-label={facts.fit.key} title="From the mail list — a guide, not a rule"
          style={{display:"inline-flex",alignItems:"center",padding:"3px 10px",borderRadius:20,fontSize:11.5,fontWeight:700,color:C.navy,background:C.cream,border:"1px solid "+C.rule,maxWidth:"100%",overflowWrap:"anywhere"}}>
          {fitLabelText(facts.fit)}
        </span>
      </div>}
      {lines.length>0?<div id={regionId} data-facts-lines="" style={{display:"flex",flexDirection:"column",gap:5}}>
        {groups.map(function(g){
          return <React.Fragment key={g.key}>
            {g.key==="history"&&hasProperty&&<div data-facts-group="history" style={Object.assign({},GROUP,{paddingTop:6,borderTop:"1px solid "+C.borderLight})}>History with us</div>}
            <dl data-facts-list={g.key} style={{display:"flex",flexDirection:"column",gap:5,margin:0}}>{g.lines.map(line)}</dl>
          </React.Fragment>;
        })}
      </div>:<div style={{fontSize:12,color:C.textMuted}}>Nothing else on file for this lead yet.</div>}
      {extra>0&&<button type="button" data-facts-toggle="" aria-expanded={open} aria-controls={regionId} onClick={toggle}
        style={{marginTop:8,padding:"5px 12px",minHeight:30,borderRadius:8,border:"1px solid "+C.border,background:"transparent",color:C.accentLight,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>
        {open?"Show fewer ▴":"Show all "+lines.length+" ▾"}
      </button>}
    </div>
  );
  if(!title)return body;
  return(
    <section data-on-file="" aria-label={ON_FILE_TITLE}
      style={Object.assign({background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"12px 14px",marginBottom:16,minWidth:0},style||{})}>
      <div style={{fontSize:11,fontWeight:800,color:C.accentLight,textTransform:"uppercase",letterSpacing:.7,marginBottom:8}}>{ON_FILE_TITLE}</div>
      {body}
    </section>
  );
}
